import { useMemo } from 'react';
import { useAppSelector } from '../store/hooks';
import { useGlobalFacultyFilter } from './useGlobalFacultyFilter';
import { useChartYearContext } from '../contexts/chart-year-context';
import type { ChartExcelContext } from '../utils/chartExcel';

/**
 * Builds the filter context stamped onto every chart Excel export.
 *
 * Khoa and năm have deliberately different scopes:
 * - Khoa is GLOBAL. `FacultyFilter` lives in the app-wide side menu, so it
 *   applies to every chart on every page, dashboard or not.
 * - Năm is per-surface. Only the dashboard publishes one (via ChartYearProvider);
 *   the choropleth and the AdmissionMethods hosts own their own year state and
 *   pass it in through `yearOverride`.
 *
 * `selectedFaculty` holds a faculty CODE; the export needs a readable label, so
 * it is resolved against the loaded faculty list here rather than in each of the
 * dozen call sites.
 */
export function useChartExcelContext(yearOverride?: number | string | null): ChartExcelContext {
	const { selectedFaculty } = useGlobalFacultyFilter();
	const faculties = useAppSelector((state) => state.filters.faculties);
	const contextYear = useChartYearContext();

	return useMemo(() => {
		// `faculties?` is not defensive noise: Redux `preloadedState` replaces a whole slice
		// instead of deep-merging, so a test seeding only `selectedFaculty` leaves it undefined.
		// Falling back to the raw code beats a blank cell, which would read as "unknown".
		const khoa = selectedFaculty
			? (faculties?.find((f) => f.value === selectedFaculty)?.label ?? selectedFaculty)
			: null;

		const nam = yearOverride !== undefined ? yearOverride : (contextYear ?? null);

		return { khoa, nam };
	}, [selectedFaculty, faculties, contextYear, yearOverride]);
}
